import styled from 'styled-components';
import { FieldArray, useFormikContext } from 'formik';
import {
    FormControlLabel,
    Checkbox,
    RadioGroup,
    Radio,
    FormHelperText,
    withStyles,
} from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import { Button, DatepickerSinglePicker, DatepickerTextField, TextField } from '@avangard/ui/core';
import { gray, red } from '@avangard/ui/colors';

import { getRecreationGroupLabel } from '@modules/students/helpers';
import { StudentOnlineApplicationBlock } from '@modules/students/moleculas';
import { StudentParentEntity } from '@modules/students/entities';
import { StyledFormLabel, FormRow, TextFieldWithHiddenLabel } from './styled';

import type { FormikErrors } from 'formik';
import type { OnDateChangeProps } from '@avangard/ui/core';
import type {
    UpdateStudentOnlineApplicationFormValues,
    UpdateStudentOnlineApplicationSummaryCriticalContact,
} from '@modules/students/requests';

const CRITICAL_CONTACT_FORM_GROUP = 'criticalContacts';

type renderCriticalContactRowProps = {
    criticalContact: UpdateStudentOnlineApplicationSummaryCriticalContact;
    index: number;
    onRemoveCriticalContact: () => void;

    error?: FormikErrors<UpdateStudentOnlineApplicationSummaryCriticalContact>;
};

const addIcon = <Add style={{ fontSize: 16 }} />;
const removeIcon = <Delete style={{ fontSize: 16 }} />;

const StudentOnlineApplicationSummaryStep = () => {
    const { values, errors, setFieldValue, handleChange, handleBlur } =
        useFormikContext<UpdateStudentOnlineApplicationFormValues>();

    const isMinor = !values.isAdult && !values.isFutureAdult;

    const handleChangeDate = (field: string) => (data: OnDateChangeProps) =>
        setFieldValue(field, data.date);

    const handleChangeCriticalContactDate =
        (name: string, index: number) => (data: OnDateChangeProps) => {
            const field = `summary.${CRITICAL_CONTACT_FORM_GROUP}[${index}][${name}]`;

            setFieldValue(field, data.date);
        };

    const handleChangeCriticalContactField =
        (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = event.target;

            const field = `summary.${CRITICAL_CONTACT_FORM_GROUP}[${index}][${name}]`;

            setFieldValue(field, value);
        };

    const criticalContactsValues = values.summary[CRITICAL_CONTACT_FORM_GROUP];
    const criticalContactsErrors = errors.summary?.[CRITICAL_CONTACT_FORM_GROUP];

    const isAdultForSpecialsGroupLabel = values.isAdult || values.isFutureAdult;

    const renderParentRow = (rowProps: renderCriticalContactRowProps) => {
        const { criticalContact, index, onRemoveCriticalContact, error } = rowProps;

        return (
            <CriticalContactRow key={index}>
                <FormRow grid>
                    <TextField
                        required
                        fullWidth
                        name='name'
                        label='ФИО родителя (законного представителя)'
                        placeholder='Введите ФИО'
                        value={criticalContact.name}
                        error={!!error?.name}
                        helperText={error?.name}
                        onChange={handleChangeCriticalContactField(index)}
                        onBlur={handleBlur}
                    />

                    {isMinor ? (
                        <DatepickerSinglePicker
                            date={criticalContact.birthdate}
                            onDateChange={handleChangeCriticalContactDate('birthdate', index)}
                        >
                            {datepickerProps => {
                                const { inputProps, inputComponent } = datepickerProps;

                                return (
                                    <DatepickerTextField
                                        required
                                        name='birthdate'
                                        label='Дата рождения'
                                        placeholder='ДД.ММ.ГГГГ'
                                        error={!!error?.birthdate}
                                        helperText={error?.birthdate}
                                        InputProps={{ inputComponent }}
                                        {...inputProps}
                                    />
                                );
                            }}
                        </DatepickerSinglePicker>
                    ) : (
                        <TextField
                            fullWidth
                            required
                            name='phone'
                            label='Мобильный телефон'
                            placeholder='Введите телефон'
                            value={criticalContact.phone}
                            error={!!error?.phone}
                            helperText={error?.phone}
                            onChange={handleChangeCriticalContactField(index)}
                            onBlur={handleBlur}
                        />
                    )}
                </FormRow>

                {isMinor ? (
                    <>
                        <FormRow grid>
                            <TextField
                                fullWidth
                                required
                                name='passportSerial'
                                label='Паспорт родителя (законного представителя)'
                                placeholder='Серия'
                                value={criticalContact.passportSerial}
                                error={!!error?.passportSerial}
                                helperText={error?.passportSerial}
                                onChange={handleChangeCriticalContactField(index)}
                                onBlur={handleBlur}
                            />

                            <TextFieldWithHiddenLabel
                                fullWidth
                                name='passportNumber'
                                label='Паспорт родителя (законного представителя)'
                                placeholder='№'
                                value={criticalContact.passportNumber}
                                error={!!error?.passportNumber}
                                helperText={error?.passportNumber}
                                onChange={handleChangeCriticalContactField(index)}
                                onBlur={handleBlur}
                            />
                        </FormRow>

                        <FormRow>
                            <TextField
                                fullWidth
                                required
                                name='passportIssued'
                                label='Кем и когда выдан паспорт'
                                placeholder='Введите кем и когда выдан паспорт'
                                value={criticalContact.passportIssued}
                                error={!!error?.passportIssued}
                                helperText={error?.passportIssued}
                                onChange={handleChangeCriticalContactField(index)}
                                onBlur={handleBlur}
                            />
                        </FormRow>

                        <FormRow>
                            <TextField
                                fullWidth
                                required
                                name='passportAddress'
                                label='Адрес регистрации'
                                placeholder='Введите адрес регистрации'
                                value={criticalContact.passportAddress}
                                error={!!error?.passportAddress}
                                helperText={error?.passportAddress}
                                onChange={handleChangeCriticalContactField(index)}
                                onBlur={handleBlur}
                            />
                        </FormRow>

                        <FormRow>
                            <TextField
                                fullWidth
                                required
                                name='address'
                                label='Адрес фактического проживания'
                                placeholder='Введите адрес проживания'
                                value={criticalContact.address}
                                error={!!error?.address}
                                helperText={error?.address}
                                onChange={handleChangeCriticalContactField(index)}
                                onBlur={handleBlur}
                            />
                        </FormRow>

                        <FormRow grid>
                            <TextField
                                fullWidth
                                required
                                name='phone'
                                label='Мобильный телефон'
                                placeholder='Введите телефон'
                                value={criticalContact.phone}
                                error={!!error?.phone}
                                helperText={error?.phone}
                                onChange={handleChangeCriticalContactField(index)}
                                onBlur={handleBlur}
                            />

                            <TextField
                                fullWidth
                                required
                                name='email'
                                label='E-mail'
                                placeholder='Введите e-mail'
                                value={criticalContact.email}
                                error={!!error?.email}
                                helperText={error?.email}
                                onChange={handleChangeCriticalContactField(index)}
                                onBlur={handleBlur}
                            />
                        </FormRow>
                    </>
                ) : null}

                {criticalContactsValues.length > 1 ? (
                    <CriticalContactActions>
                        <Button startIcon={removeIcon} onClick={onRemoveCriticalContact}>
                            Удалить контакт
                        </Button>
                    </CriticalContactActions>
                ) : null}
            </CriticalContactRow>
        );
    };

    return (
        <>
            {isMinor ? (
                <StudentOnlineApplicationBlock
                    title='Заполняющий анкету (законный представитель)'
                    description={<p>Укажите, кто заполняет анкету</p>}
                >
                    <Content>
                        <FormRow grid>
                            <TextField
                                required
                                fullWidth
                                id='summary.responsible.fullName'
                                label='ФИО родителя (законного представителя)'
                                placeholder='Введите ФИО'
                                value={values.summary.responsible.fullName}
                                error={!!errors.summary?.responsible?.fullName}
                                helperText={errors.summary?.responsible?.fullName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />

                            <DatepickerSinglePicker
                                date={values.summary.responsible.birthdate}
                                onDateChange={handleChangeDate('summary.responsible.birthdate')}
                            >
                                {datepickerProps => {
                                    const { inputProps, inputComponent } = datepickerProps;

                                    return (
                                        <DatepickerTextField
                                            required
                                            id='summary.responsible.birthdate'
                                            label='Дата рождения'
                                            placeholder='ДД.ММ.ГГГГ'
                                            error={!!errors.summary?.responsible?.birthdate}
                                            helperText={errors.summary?.responsible?.birthdate}
                                            InputProps={{ inputComponent }}
                                            {...inputProps}
                                        />
                                    );
                                }}
                            </DatepickerSinglePicker>
                        </FormRow>

                        <FormRow grid>
                            <TextField
                                required
                                fullWidth
                                id='summary.responsible.passportSerial'
                                label='Паспорт родителя (законного представителя)'
                                placeholder='Серия'
                                value={values.summary.responsible.passportSerial}
                                error={!!errors.summary?.responsible?.passportSerial}
                                helperText={errors.summary?.responsible?.passportSerial}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />

                            <TextFieldWithHiddenLabel
                                fullWidth
                                id='summary.responsible.passportNumber'
                                label='Паспорт родителя (законного представителя)'
                                placeholder='№'
                                value={values.summary.responsible.passportNumber}
                                error={!!errors.summary?.responsible?.passportNumber}
                                helperText={errors.summary?.responsible?.passportNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </FormRow>

                        <FormRow>
                            <TextField
                                required
                                fullWidth
                                id='summary.responsible.passportIssued'
                                label='Кем и когда выдан паспорт'
                                placeholder='Введите кем и когда выдан паспорт'
                                value={values.summary.responsible.passportIssued}
                                error={!!errors.summary?.responsible?.passportIssued}
                                helperText={errors.summary?.responsible?.passportIssued}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </FormRow>

                        <FormRow>
                            <TextField
                                required
                                fullWidth
                                id='summary.responsible.passportAddress'
                                label='Адрес регистрации'
                                placeholder='Введите адрес регистрации'
                                value={values.summary.responsible.passportAddress}
                                error={!!errors.summary?.responsible?.passportAddress}
                                helperText={errors.summary?.responsible?.passportAddress}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </FormRow>

                        <FormRow>
                            <TextField
                                required
                                fullWidth
                                id='summary.responsible.address'
                                label='Адрес проживания'
                                placeholder='Введите адрес проживания'
                                value={values.summary.responsible.address}
                                error={!!errors.summary?.responsible?.address}
                                helperText={errors.summary?.responsible?.address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </FormRow>

                        <FormRow grid>
                            <TextField
                                required
                                fullWidth
                                id='summary.responsible.phone'
                                label='Мобильный телефон'
                                placeholder='Введите телефон'
                                value={values.summary.responsible.phone}
                                error={!!errors.summary?.responsible?.phone}
                                helperText={errors.summary?.responsible?.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />

                            <TextField
                                required
                                fullWidth
                                id='summary.responsible.email'
                                label='E-mail'
                                placeholder='Введите e-mail'
                                value={values.summary.responsible.email}
                                error={!!errors.summary?.responsible?.email}
                                helperText={errors.summary?.responsible?.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </FormRow>

                        <FormRow>
                            <StyledFormControlLabel
                                label={
                                    <p>
                                        Я несу ответственность за внесенные данные в анкету
                                        <span>*</span>
                                    </p>
                                }
                                control={
                                    <Checkbox
                                        id='summary.responsible.isResponsibleChecked'
                                        name='summary.responsible.isResponsibleChecked'
                                        checked={values.summary.responsible.isResponsibleChecked}
                                        onChange={handleChange}
                                    />
                                }
                            />

                            {!!errors.summary?.responsible?.isResponsibleChecked ? (
                                <StyledFormHelperText error>
                                    {errors.summary.responsible.isResponsibleChecked}
                                </StyledFormHelperText>
                            ) : null}
                        </FormRow>
                    </Content>
                </StudentOnlineApplicationBlock>
            ) : null}

            <StudentOnlineApplicationBlock title='Общая информация обучающегося'>
                <Content>
                    {isMinor ? (
                        <>
                            <FormRow>
                                <TextField
                                    disabled
                                    required
                                    fullWidth
                                    id='summary.main.fullName'
                                    label='ФИО'
                                    value={values.summary.main.fullName}
                                />
                            </FormRow>

                            <FormRow grid>
                                <TextField
                                    required
                                    fullWidth
                                    id='summary.main.fullNameGenitive'
                                    label='ФИО (в родительном падеже)'
                                    placeholder='Иванова Ивана Ивановича'
                                    value={values.summary.main.fullNameGenitive}
                                    error={!!errors.summary?.main?.fullNameGenitive}
                                    helperText={errors.summary?.main?.fullNameGenitive}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />

                                <DatepickerSinglePicker
                                    date={values.summary.main.birthdate}
                                    onDateChange={handleChangeDate('summary.main.birthdate')}
                                >
                                    {datepickerProps => {
                                        const { inputProps, inputComponent } = datepickerProps;

                                        return (
                                            <DatepickerTextField
                                                required
                                                disabled
                                                id='summary.main.birthdate'
                                                label='Дата рождения'
                                                InputProps={{ inputComponent }}
                                                {...inputProps}
                                            />
                                        );
                                    }}
                                </DatepickerSinglePicker>
                            </FormRow>
                        </>
                    ) : (
                        <>
                            <FormRow grid>
                                <TextField
                                    disabled
                                    required
                                    fullWidth
                                    id='summary.main.fullName'
                                    label='ФИО'
                                    value={values.summary.main.fullName}
                                />

                                <DatepickerSinglePicker
                                    date={values.summary.main.birthdate}
                                    onDateChange={handleChangeDate('summary.main.birthdate')}
                                >
                                    {datepickerProps => {
                                        const { inputProps, inputComponent } = datepickerProps;

                                        return (
                                            <DatepickerTextField
                                                required
                                                disabled
                                                id='summary.main.birthdate'
                                                label='Дата рождения'
                                                InputProps={{ inputComponent }}
                                                {...inputProps}
                                            />
                                        );
                                    }}
                                </DatepickerSinglePicker>
                            </FormRow>
                        </>
                    )}

                    <FormRow>
                        <TextField
                            required
                            fullWidth
                            id='summary.main.passportAddress'
                            label='Адрес регистрации'
                            placeholder='Введите адрес регистрации'
                            value={values.summary.main.passportAddress}
                            error={!!errors.summary?.main?.passportAddress}
                            helperText={errors.summary?.main?.passportAddress}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </FormRow>

                    <FormRow>
                        <TextField
                            required
                            fullWidth
                            id='summary.main.address'
                            label='Адрес фактического проживания'
                            placeholder='Введите адрес проживания'
                            value={values.summary.main.address}
                            error={!!errors.summary?.main?.address}
                            helperText={errors.summary?.main?.address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </FormRow>

                    <FormRow grid>
                        <TextField
                            required
                            fullWidth
                            id='summary.main.passportSerial'
                            label='Паспорт обучающегося'
                            placeholder='Серия'
                            value={values.summary.main.passportSerial}
                            error={!!errors.summary?.main?.passportSerial}
                            helperText={errors.summary?.main?.passportSerial}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <TextFieldWithHiddenLabel
                            fullWidth
                            id='summary.main.passportNumber'
                            label='Паспорт обучающегося'
                            placeholder='№'
                            value={values.summary.main.passportNumber}
                            error={!!errors.summary?.main?.passportNumber}
                            helperText={errors.summary?.main?.passportNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </FormRow>

                    <FormRow>
                        <TextField
                            required
                            fullWidth
                            id='summary.main.passportIssued'
                            label='Кем и когда выдан паспорт'
                            placeholder='Введите кем и когда выдан паспорт'
                            value={values.summary.main.passportIssued}
                            error={!!errors.summary?.main?.passportIssued}
                            helperText={errors.summary?.main?.passportIssued}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </FormRow>

                    <FormRow grid>
                        <TextField
                            required
                            fullWidth
                            disabled
                            id='summary.main.schoolName'
                            label='Наименование образов. организации'
                            value={values.summary.main.schoolName}
                        />

                        <TextField
                            required
                            fullWidth
                            disabled
                            id='summary.main.studyYear'
                            label='Год обучения'
                            value={values.summary.main.studyYear}
                        />
                    </FormRow>

                    <FormRow grid>
                        <TextField
                            required
                            fullWidth
                            id='summary.main.phone'
                            label='Мобильный телефон'
                            placeholder='Введите телефон'
                            value={values.summary.main.phone}
                            error={!!errors.summary?.main?.phone}
                            helperText={errors.summary?.main?.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <TextField
                            required
                            fullWidth
                            disabled
                            id='summary.main.email'
                            label='E-mail'
                            value={values.summary.main.email}
                        />
                    </FormRow>

                    <FormRow grid>
                        <TextField
                            fullWidth
                            id='summary.main.vk'
                            label='ВКонтакте'
                            placeholder='vk.com/id123456'
                            value={values.summary.main.vk}
                            error={!!errors.summary?.main?.vk}
                            helperText={errors.summary?.main?.vk}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <TextField
                            fullWidth
                            id='summary.main.telegram'
                            label='Telegram'
                            placeholder='@telegram'
                            value={values.summary.main.telegram}
                            error={!!errors.summary?.main?.telegram}
                            helperText={errors.summary?.main?.telegram}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </FormRow>
                </Content>
            </StudentOnlineApplicationBlock>

            <StudentOnlineApplicationBlock
                title='Контакты родителей'
                description={<p>Или законных представителей для связи в экстренной ситуации</p>}
            >
                <Content>
                    <FieldArray name='summary.criticalContacts'>
                        {arrayHelpers => {
                            const handleClickAddRow = (): void =>
                                arrayHelpers.push(new StudentParentEntity({}));

                            const handleClickRemoveRow = (index: number) => (): void =>
                                arrayHelpers.remove(index);

                            const hasParents = criticalContactsValues.length > 0;

                            return (
                                <>
                                    <CriticalContactsGroup>
                                        {hasParents
                                            ? criticalContactsValues.map(
                                                  (criticalContact, index) => {
                                                      const error = criticalContactsErrors?.[
                                                          index
                                                      ] as
                                                          | FormikErrors<UpdateStudentOnlineApplicationSummaryCriticalContact>
                                                          | undefined;

                                                      return renderParentRow({
                                                          criticalContact,
                                                          index,
                                                          error,
                                                          onRemoveCriticalContact:
                                                              handleClickRemoveRow(index),
                                                      });
                                                  },
                                              )
                                            : null}
                                    </CriticalContactsGroup>

                                    <FormRow>
                                        <Button
                                            variant='outlined'
                                            startIcon={addIcon}
                                            onClick={handleClickAddRow}
                                        >
                                            Добавить контакт
                                        </Button>
                                    </FormRow>
                                </>
                            );
                        }}
                    </FieldArray>
                </Content>
            </StudentOnlineApplicationBlock>

            <StudentOnlineApplicationBlock title='Отношение к самостоятельному отдыху'>
                <Content>
                    <FormRow>
                        <StyledFormLabel required>
                            {getRecreationGroupLabel('selfLeft', isAdultForSpecialsGroupLabel)}
                        </StyledFormLabel>

                        <RadioGroup
                            row
                            name='summary.recreation.selfLeft'
                            value={values.summary.recreation.selfLeft}
                            onChange={handleChange}
                        >
                            <FormControlLabel value='0' label='Нет' control={<Radio />} />
                            <FormControlLabel value='1' label='Да' control={<Radio />} />
                        </RadioGroup>

                        {!!errors.summary?.recreation?.selfLeft ? (
                            <StyledFormHelperText error>
                                {errors.summary.recreation.selfLeft}
                            </StyledFormHelperText>
                        ) : null}
                    </FormRow>

                    {values.summary.recreation.selfLeft === '1' ? (
                        <>
                            <FormRow grid>
                                <TextField
                                    fullWidth
                                    required
                                    id='summary.recreation.count'
                                    label='Cколько раз'
                                    placeholder='Укажите сколько раз'
                                    value={values.summary.recreation.count}
                                    error={!!errors.summary?.recreation?.count}
                                    helperText={errors.summary?.recreation?.count}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />

                                <TextField
                                    fullWidth
                                    required
                                    id='summary.recreation.fromAge'
                                    label='Начиная с какого возраста'
                                    placeholder='Укажите с какого возраста'
                                    value={values.summary.recreation.fromAge}
                                    error={!!errors.summary?.recreation?.fromAge}
                                    helperText={errors.summary?.recreation?.fromAge}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </FormRow>

                            <FormRow>
                                <TextField
                                    fullWidth
                                    id='summary.recreation.assesment'
                                    label='Оценка проведенного отдыха'
                                    placeholder='Например: Отдых был активным и интересным'
                                    value={values.summary.recreation.assesment}
                                    error={!!errors.summary?.recreation?.assesment}
                                    helperText={errors.summary?.recreation?.assesment}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </FormRow>
                        </>
                    ) : null}

                    <FormRow>
                        <TextField
                            fullWidth
                            required
                            id='summary.recreation.mood'
                            label={getRecreationGroupLabel('mood', isAdultForSpecialsGroupLabel)}
                            placeholder='Например: С лёгким волнением, но позитивным настроем'
                            value={values.summary.recreation.mood}
                            error={!!errors.summary?.recreation?.mood}
                            helperText={errors.summary?.recreation?.mood}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </FormRow>
                </Content>
            </StudentOnlineApplicationBlock>
        </>
    );
};

const StyledFormControlLabel = withStyles(() => ({
    label: {
        '& span': {
            marginLeft: 4,
            color: red[100],
        },
    },
}))(FormControlLabel);

const StyledFormHelperText = withStyles(() => ({
    root: {
        width: '100%',
    },
}))(FormHelperText);

const Content = styled.div`
    & > *:not(:last-child) {
        margin-bottom: 24px;
    }
`;

const CriticalContactsGroup = styled.div`
    width: 100%;

    > *:not(:last-child) {
        margin-bottom: 12px;
        padding-bottom: 24px;
        border-bottom: 1px solid ${gray[40]};
    }
`;

const CriticalContactRow = styled.div`
    width: 100%;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 24px;
        }
    }
`;

const CriticalContactActions = styled(FormRow)`
    justify-content: flex-end;
`;

export { StudentOnlineApplicationSummaryStep };
